import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import { useGetWorkplace } from "@src/appV2/FacilityProfile/api/useGetWorkplace";
import { useGetAttendanceVerification } from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/api/useGetAttendanceVerification";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { getCredentialVariant } from "@src/appV2/WorkerCredentials/utils/getCredentialVariant";
import { useMemo } from "react";

import { type ExtendedExternalIntegrationType, ExtendedExternalIntegrationTypes } from "../types";
import { useGetWorkplaceExternalIntegrations } from "./useGetWorkplaceExternalIntegrations";

const EXTENDED_EXTERNAL_INTEGRATION_TYPES_SET = new Set<string>(
  Object.values(ExtendedExternalIntegrationTypes)
);

function isExtendedExternalIntegrationType(type: string): type is ExtendedExternalIntegrationType {
  return EXTENDED_EXTERNAL_INTEGRATION_TYPES_SET.has(type);
}

interface UseGetWorkplaceEnabledConfigurationsParams {
  workplaceId: string;
}

export function useGetWorkplaceEnabledConfigurations(
  params: UseGetWorkplaceEnabledConfigurationsParams
): ExtendedExternalIntegrationType[] {
  const { workplaceId } = params;

  const { data: externalIntegrations } = useGetWorkplaceExternalIntegrations({
    workplaceId,
    filterEnabled: true,
  });

  const { data: attendanceVerification } = useGetAttendanceVerification(workplaceId);

  const { data: workplaceProfile } = useGetWorkplace({ workplaceId });

  const { enabled: touchpointIntegrationEnabled } = useCbhFlag(
    CbhFeatureFlag.TOUCHPOINT_INTEGRATION,
    {
      defaultValue: {
        enabled: false,
      },
    }
  );

  const isTimeclockComplianceEnabled = useMemo(() => {
    const methods = attendanceVerification?.data?.attributes?.methods ?? [];
    return methods.some(
      (method) =>
        [
          AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
          AttendanceVerificationMethod.TAKE_A_VIDEO,
        ].includes(method.type) && method.isEnabled
    );
  }, [attendanceVerification]);

  const { timeclockComplianceType } = getCredentialVariant(workplaceProfile);

  return useMemo(() => {
    const types: ExtendedExternalIntegrationType[] = (externalIntegrations ?? [])
      .filter((integration) => integration.isEnabled)
      .map((integration): string => integration.type)
      .filter(isExtendedExternalIntegrationType);

    if (touchpointIntegrationEnabled) {
      types.push(ExtendedExternalIntegrationTypes.MESH);
    }

    if (
      isTimeclockComplianceEnabled &&
      !types.includes(ExtendedExternalIntegrationTypes.TIMECLOCK_COMPLIANCE) &&
      !types.includes(ExtendedExternalIntegrationTypes.TIMECLOCK_COMPLIANCE_PIN_PLUS_PASSWORD)
    ) {
      types.push(timeclockComplianceType);
    }

    return types;
  }, [
    externalIntegrations,
    touchpointIntegrationEnabled,
    isTimeclockComplianceEnabled,
    timeclockComplianceType,
  ]);
}
